import { motion } from "framer-motion";
import { Layout } from "@/components/layout/Layout";
import {
  Sparkles,
  Zap,
  Shield,
  Video,
  Wand2,
  Smartphone,
  Cloud,
  Lock,
  RefreshCw,
  Users,
  Clock,
} from "lucide-react";

const mainFeatures = [
  {
    icon: Video,
    title: "3 Vídeos por Crédito",
    description:
      "Cada task gera 3 vídeos UGC únicos com roteiros diferentes. Otimizados pra TikTok, Reels e Shorts. Porque a gente sabe que você é indeciso.",
    gradient: "from-yellow to-yellow-glow",
  },
  {
    icon: Sparkles,
    title: "Imagem 9:16 Automática",
    description:
      "Sua imagem de produto é adaptada pro formato vertical ideal. Sem você precisar abrir o Canva, sem sofrer com crop.",
    gradient: "from-yellow to-gold",
  },
];

const allFeatures = [
  {
    icon: Smartphone,
    title: "Interface Intuitiva",
    description: "Design simples que até seu estagiário consegue usar.",
  },
  {
    icon: Cloud,
    title: "Processamento na Nuvem",
    description: "Gera vídeos pesados sem travar seu notebook.",
  },
  {
    icon: Lock,
    title: "Dados Protegidos",
    description: "Criptografia e proteção de dados. Sério.",
  },
  {
    icon: RefreshCw,
    title: "Reprocessamento Gratuito",
    description: "Se algo falhar, reprocessa de graça. A gente não cobra por bug.",
  },
  {
    icon: Users,
    title: "Sistema de Indicações",
    description: "Indica amigos, ganha créditos. Capitalismo de qualidade.",
  },
  {
    icon: Clock,
    title: "Download por 7 Dias",
    description: "Vídeos ficam disponíveis por 7 dias. Baixa logo.",
  },
  {
    icon: Zap,
    title: "Renderização Rápida",
    description: "Vídeos prontos em minutos, não em horas.",
  },
  {
    icon: Shield,
    title: "Qualidade Garantida",
    description: "Vídeos em alta qualidade prontos pra publicar.",
  },
  {
    icon: Sparkles,
    title: "IA Sempre Atualizada",
    description: "Modelos de IA melhorados constantemente. Cada vez mais real.",
  },
];

const demoMedia = {
  productName: "Brinco de ouro feminino",
  imageSrc: "/imagem_demo.png",
  videoSrc: "/video_demo.mp4",
};

export default function Funcionalidades() {
  return (
    <Layout>
      {/* Hero */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,hsl(var(--yellow)/0.1),transparent_50%)]" />
        <div className="container mx-auto px-4 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Funcionalidades que <span className="text-gradient">fazem o trabalho</span>
            </h1>
            <p className="text-lg text-muted-foreground">
              Tudo que você precisa pra escalar seus criativos sem estresse. O Publee faz, você lucra.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Features */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="space-y-20">
            {mainFeatures.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className={`grid lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? "lg:flex-row-reverse" : ""
                }`}
              >
                <div className={index % 2 === 1 ? "lg:order-2" : ""}>
                  <div
                    className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center mb-6`}
                  >
                    <feature.icon className="w-8 h-8 text-primary-foreground" />
                  </div>
                  <h2 className="text-3xl md:text-4xl font-bold mb-4">
                    {feature.title}
                  </h2>
                  <p className="text-lg text-muted-foreground leading-relaxed">
                    {feature.description}
                  </p>
                </div>
                <div
                  className={`${
                    index % 2 === 1 ? "lg:order-1" : ""
                  } relative`}
                >
                  <div
                    className={`rounded-2xl gradient-card border border-border overflow-hidden ${
                      feature.title === "3 Vídeos por Crédito" ||
                      feature.title === "Imagem 9:16 Automática"
                        ? "aspect-[9/16] max-w-xs mx-auto"
                        : "aspect-video"
                    }`}
                  >
                    {feature.title === "3 Vídeos por Crédito" ? (
                      <video
                        src={demoMedia.videoSrc}
                        controls
                        playsInline
                        preload="metadata"
                        poster={demoMedia.imageSrc}
                        className="w-full h-full object-cover"
                      />
                    ) : feature.title === "Imagem 9:16 Automática" ? (
                      <img
                        src={demoMedia.imageSrc}
                        alt={`${demoMedia.productName} - imagem demonstrativa`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div
                          className={`w-24 h-24 rounded-full bg-gradient-to-br ${feature.gradient} flex items-center justify-center animate-pulse-glow`}
                        >
                          <feature.icon className="w-12 h-12 text-primary-foreground" />
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="absolute -z-10 inset-0 blur-3xl opacity-20 bg-gradient-to-br from-yellow to-gold" />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* All Features Grid */}
      <section className="py-20 bg-card">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              E muito <span className="text-gradient">mais</span>
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Uma suíte completa de ferramentas pra profissionalizar sua
              produção de conteúdo.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {allFeatures.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className="p-6 rounded-xl bg-secondary/50 border border-border hover:border-primary/30 transition-all duration-300 group"
              >
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                  <feature.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
                <p className="text-sm text-muted-foreground">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
}
